import React from "react";
import { Row, Col, Table, Badge, Button } from "react-bootstrap";
import "./AllocatedERA.css";
import { useNavigate } from "react-router-dom";

export default function AllocatedERA({ eraAllocated = [] }) {
  const navigate = useNavigate();

  if (!Array.isArray(eraAllocated) || eraAllocated.length === 0) {
    return (
      <p className="text-muted fst-italic small ms-2 mt-2">
        No matched ERA claims found.
      </p>
    );
  }

  const formatCurrency = (x) =>
    x || x === 0
      ? new Intl.NumberFormat("en-ZA", {
          style: "currency",
          currency: "ZAR",
          minimumFractionDigits: 2,
        }).format(x)
      : "";

  return (
    <div className="allocated-era">
      {eraAllocated.map((claim, idx) => {
        const e = claim.eraClaim || {};
        const m = claim.matchedClaim || {};
        const a = claim.amounts || {};

        return (
          <div
            key={idx}
            className="p-2 mt-1 bg-white border-bottom border-secondary rounded"
            style={{ fontSize: "0.9rem" }}
          >
            {/* ---- Summary Layout ---- */}
            <Row className="mb-1 mt-1">
              {/* --- Column 1: Patient Info --- */}
              <Col md={3}>
                <div className="fw-semibold text-primary">
                  {e.patientFirstName} {e.patientLastName}{" "}
                </div>
                <div className="small">
                  Medical Aid #:{" "}
                  <span className="fw-semibold">
                    {e.accountMedicalAidNumber || "-"}{" "}
                  </span>
                  {e.dependantCode && (
                    <span className="fw-semibold">({e.dependantCode})</span>
                  )}
                </div>
                <div className="small">
                  Treatment Date:{" "}
                  {e.treatmentDate
                    ? new Date(e.treatmentDate).toLocaleDateString()
                    : "-"}
                </div>
              </Col>

              {/* --- Column 2: Practice + Invoice Info --- */}
              <Col md={3}>
                <div className="fw-semibold text-primary">
                  {e.claimPracticeName} ({e.claimPracticeNumber})
                </div>

                <div className="small">
                  Transaction #:{" "}
                  <span className="text-muted">
                    {e.transactionNumber || "-"}
                  </span>
                </div>
              </Col>

              {/* --- Column 3: Status + Totals --- */}
              <Col md={4}>
                

                <div className="small">
                  <Row className="mb-1">
                    <Col xs={6} className="text-primary fw-semibold text-end">
                      Total Claimed:
                    </Col>
                    <Col xs={2} className="text-end">
                      {formatCurrency(a.totalClaimed || 0)}
                    </Col>
                  </Row>

                  <Row className="mb-1">
                    <Col xs={6} className="text-primary fw-semibold text-end">
                      Total Paid:
                    </Col>
                    <Col xs={2} className="text-end">
                      {formatCurrency(a.totalPaid || 0)}
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={6} className="text-primary fw-semibold text-end">
                      Moved to Patient:
                    </Col>
                    <Col xs={2} className="text-end">
                      {formatCurrency(a.movedToPatient || 0)}
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* --- Column 4: Action Buttons --- */}
              <Col
                md={2}
                className="d-flex flex-column align-items-end justify-content-start gap-1"
              >
                <div className="fw-semibold text-primary mb-1">
                  Status:{" "}
                  <Badge bg="success" className="fw-semibold">
                    {m.statusAfter || "Finalised"}
                  </Badge>
                </div>
                {/* Manually-allocated entries have no invoiceId — point at
                    the account instead. Auto-matched entries keep the
                    existing Invoice deep link. */}
                {claim?.manuallyAllocated || !m.invoiceId ? (
                  m.accountId ? (
                    <Button
                      variant="primary"
                      size="sm"
                      className="fw-semibold shadow-sm w-75"
                      onClick={() => navigate(`/accounts/${m.accountId}`)}
                    >
                      <i className="fa-solid fa-user me-2"></i>Account
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </Button>
                  ) : null
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    className="fw-semibold shadow-sm w-75"
                    onClick={() => navigate(`/invoices/posted/${m.invoiceId}`)}
                  >
                    <i className="fa-solid fa-file-invoice me-2"></i>Invoice
                    <i className="fa-solid fa-arrow-right ms-2"></i>
                  </Button>
                )}
             {/*}   <Button
                  variant="outline-primary"
                  size="sm"
                  className="fw-semibold shadow-sm w-75"
                  onClick={() =>
                    console.log("Receipt clicked for:", m.invoiceId)
                  }
                >
                  <i className="fa-solid fa-receipt me-2"></i>Receipt
                  <i className="fa-solid fa-download ms-2"></i>
                </Button> */}
              </Col>
            </Row>

            {/* ---- Line Items Table ---- */}
            {Array.isArray(claim.lines) && claim.lines.length > 0 ? (
              <Table
                bordered
                hover
                size="sm"
                responsive
                className="mb-2 align-middle"
              >
                <thead className="table-light text-center">
                  <tr>
                    <th
                      className="text-primary fw-semibold"
                      style={{ width: "5%" }}
                    >
                      Line #
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ width: "10%" }}
                    >
                      Date of Service
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ width: "10%" }}
                    >
                      Tariff Code
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ width: "10%" }}
                    >
                      Claimed
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ width: "10%" }}
                    >
                      Paid
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ width: "10%" }}
                    >
                      Patient Portion
                    </th>
                    <th
                      className="text-primary fw-semibold"
                      style={{ width: "45%" }}
                    >
                      Reason Codes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {claim.lines.map((line, i) => (
                    <tr key={i}>
                      <td className="text-center">{line.eraLineNumber}</td>
                      <td className="text-center">
                        {line.dateOfService
                          ? new Date(line.dateOfService).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="text-center">{line.tariffCode || "-"}</td>
                      <td className="text-center">
                        {formatCurrency(line.claimedAmount || 0)}
                      </td>
                      <td className="text-center">
                        {formatCurrency(line.paidAmount || 0)}
                      </td>
                      <td className="text-center">
                        {formatCurrency(line.patientPortion || 0)}
                      </td>
                      <td className="text-start">
                        {Array.isArray(line.reasonCodes) &&
                        line.reasonCodes.length > 0 ? (
                          line.reasonCodes.map((r, k) => (
                            <div key={k} className="small">
                              <Badge
                                bg="warning"
                                text="dark"
                                className="me-2 fw-semibold"
                              >
                                {r.code}
                              </Badge>
                              {r.description}
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted small mb-0">
                No lines recorded for this claim.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
